import { signalTypeMap } from "app/pages/sales-intelligence/domain/signals/types/signal";
import type { CompanyBaseInfo } from "app/pages/sales-intelligence/modules/company-analysis/types/company";
import type { SignalEventDto } from "app/pages/sales-intelligence/modules/company-analysis/types/signals";
import { isAppsSdksSignalEvent } from "app/pages/sales-intelligence/modules/company-analysis/types/signals";
import { assertNever } from "app/pages/sales-intelligence/utils/errors/assertions";

import AdNetworkSignalEventInfo from "./AdNetworkSignalEventInfo";
import AppsSignalEventInfo from "./AppsSignalEventInfo";
import IntentSignalEventInfo from "./IntentSignalEventInfo/IntentSignalEventInfo";
import NewsSignalEventInfo from "./NewsSignalEventInfo";
import TechnologySignalEventInfo from "./TechnologySignalEventInfo";
import TrafficSignalEventInfo from "./TrafficSignalEventInfo";

type SignalEventInfoProps = {
    signal: SignalEventDto;
    companyInfo?: CompanyBaseInfo;
    onCompanyLinkClick: (signalEvent: SignalEventDto) => void;
    onShowDescriptionClick: () => void;
};

const SignalEventInfo = ({ signal, companyInfo, onCompanyLinkClick, onShowDescriptionClick }: SignalEventInfoProps) => {
    const companyName = companyInfo?.name || "";

    switch (signal.type) {
        case signalTypeMap.intent:
            return <IntentSignalEventInfo signal={signal} />;
        case signalTypeMap.news:
            return (
                <NewsSignalEventInfo
                    signal={signal}
                    onCompanyLinkClick={onCompanyLinkClick}
                    onShowDescriptionClick={onShowDescriptionClick}
                    data-automation="signal-event-info-news"
                />
            );
        case signalTypeMap.technology:
            return (
                <TechnologySignalEventInfo
                    signal={signal}
                    companyName={companyName}
                    data-automation="signal-event-info-technology"
                />
            );
        case signalTypeMap.adNetwork:
            return (
                <AdNetworkSignalEventInfo
                    signal={signal}
                    companyName={companyName}
                    data-automation="signal-event-info-ad-network"
                />
            );
        case signalTypeMap.traffic:
            return <TrafficSignalEventInfo signal={signal} />;
        case signalTypeMap.apps:
            // A second Apps subtype would not be an AppsAppsSdks event; render nothing rather than mistype it.
            return isAppsSdksSignalEvent(signal) ? (
                <AppsSignalEventInfo
                    signal={signal}
                    companyName={companyName}
                    data-automation="signal-event-info-apps"
                />
            ) : null;
        case signalTypeMap.ecommerce:
            // Pre-existing gap, outside SI-9080: the overview card renders an Ecommerce body, the feed never has.
            return null;
        case signalTypeMap.roleChanges:
            // The role change is fully described by the event title and its change-type tag.
            return null;
        default:
            return assertNever(signal, "renderSignalEventBody", "SignalEventInfo");
    }
};

export default SignalEventInfo;
